/**
 * diagnostics/CoreDiagnostics.ts — centralized logging, error management, and
 * developer-facing observability for Roamer Core™.
 *
 * One place records every log line, exception, and timing sample. The
 * `snapshot()` surface is what a future /system page or dev console reads —
 * running engines, health, queued/completed events, background jobs, cache
 * stats, processing times. Developers only; never user-facing.
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogEntry {
  level: LogLevel;
  scope: string;
  message: string;
  at: number;
}

export interface TimingSample {
  label: string;
  durationMs: number;
  at: number;
}

const LOG_LIMIT = 200;
const TIMING_LIMIT = 200;

export class CoreDiagnostics {
  private logs: LogEntry[] = [];
  private timings: TimingSample[] = [];
  private errorCount = 0;

  log(level: LogLevel, scope: string, message: string, at: number): void {
    this.logs.push({ level, scope, message, at });
    if (level === 'error') this.errorCount += 1;
    if (this.logs.length > LOG_LIMIT) this.logs.shift();
  }

  /** Record a caught exception centrally (never rethrows). */
  captureError(scope: string, err: unknown, at: number): void {
    const message = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
    this.log('error', scope, message, at);
  }

  /** Record how long an operation took (performance metrics). */
  recordTiming(label: string, durationMs: number, at: number): void {
    this.timings.push({ label, durationMs, at });
    if (this.timings.length > TIMING_LIMIT) this.timings.shift();
  }

  /** Time an async operation and record it, capturing failures centrally. */
  async measure<T>(label: string, at: number, fn: () => Promise<T>): Promise<T> {
    const started = Date.now();
    try {
      return await fn();
    } catch (err) {
      this.captureError(label, err, at);
      throw err;
    } finally {
      this.recordTiming(label, Math.max(0, Date.now() - started), at);
    }
  }

  getLogs(level?: LogLevel): LogEntry[] {
    return level ? this.logs.filter((l) => l.level === level) : [...this.logs];
  }

  getTimings(): TimingSample[] {
    return [...this.timings];
  }

  averageTiming(label: string): number | null {
    const samples = this.timings.filter((t) => t.label === label);
    if (samples.length === 0) return null;
    return Math.round(samples.reduce((s, t) => s + t.durationMs, 0) / samples.length);
  }

  get totalErrors(): number {
    return this.errorCount;
  }
}
